import React, { useEffect, useState, useRef } from 'react';
import {
  AppBar,
  Button,
  Grid,
  Paper,
  Toolbar,
  Typography,
} from '@material-ui/core';
import RoutineDaysWeekSelection from './RoutineDaysWeekSelection';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import {
  getRoutineDays,
  setEdited,
  setEditedRoutineDays,
  setRoutineDays,
  setRestaurantsRoutineDays,
  setRecentlyEditedDeliveryDays,
} from '../../redux/actions/routineDaysActions';
import { useParams } from 'react-router-dom';
import { snackbarShowMessage } from '../../redux/actions/snackbarActions';
import { useTranslation } from 'react-i18next';
import RBISuccessPopup from '../RBIMessagePopup/RBISuccessPopup';
import ShowErrorMessageModal from './RestaurantDetails/ShowErrorMessageModal';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useGeneralTooltip } from '../../utils/tooltipUtils';
import RBIFollowCursorTooltip from '../RBIFollowCursorTooltip/RBIFollowCursorTooltip';
import { toggleOperationHoursViewMode } from '../../redux/actions/newOperationHoursActions';

function SupplyIntakeDays({ classes }: any) {
  const { deliveryDays, loaded } = useSelector(
    (state: RootStateOrAny) => state.RoutineDaysReducer,
  );
  const isRoutineDaysEdited = useSelector(
    (state: RootStateOrAny) => state.RoutineDaysReducer.edited,
  );

  // To capture original delivery days when page loads
  const originalDeliveryDaysRef = useRef<string[]>([]);
  const hasCapturedOriginalRef = useRef<boolean>(false);

  interface ParamTypes {
    id: string;
  }
  const { id } = useParams<ParamTypes>();
  const [successPopupModal, setSuccessPopupModal] = useState({
    isAlert: false,
    isOpen: false,
    message: '',
    shouldRedirectOnClose: false,
  });
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [uploadErrors, setErrors] = useState();
  const dispatch: any = useDispatch();

  const isMDSUpAndRunning = useSelector(
    (state: RootStateOrAny) => state.NewOperationHoursReducer.isMDSUpAndRunning,
  );
  const { ramWarningBarToggle } = useFlags();

  const { hasEditHoursOfOperation, hasViewMenuItemOperationHours } =
    useSelector((state: RootStateOrAny) => state.UserReducer.thisUser);

  const { RestaurantStatus } = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer.entity,
  );

  const { t } = useTranslation();
  const isRestaurantClosed = ['Closed', 'Temp Closed'].includes(
    RestaurantStatus,
  );

  const tooltipMessage = useGeneralTooltip({
    ramWarningBarToggle,
    isMDSUpAndRunning,
    restaurantId: id,
    isRestaurantClosed,
  });
  const { selectedRestaurantsList: selectedRestaurants } = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer,
  );

  const handleSuccessCloseModal = () => {
    const shouldRedirect = successPopupModal.shouldRedirectOnClose;

    setSuccessPopupModal({
      isAlert: false,
      isOpen: false,
      message: '',
      shouldRedirectOnClose: false,
    });

    // Only redirect to view mode for pending process success popup
    if (shouldRedirect) {
      dispatch(toggleOperationHoursViewMode(true));
    }
  };

  const updateRoutineDays = async () => {
    try {
      if (deliveryDays.length) {
        let RDResult;
        if (!id) {
          const restaurants = selectedRestaurants.map(
            restaurant => restaurant.metas?.restaurantId,
          );
          const jsonData = {
            data: {
              deliveryDays: deliveryDays,
            },
            restaurants: restaurants,
          };
          RDResult = await dispatch(setRestaurantsRoutineDays(jsonData));
        } else {
          RDResult = await dispatch(setRoutineDays(id, deliveryDays));
        }
        if (RDResult.status < 300) {
          dispatch(setEdited(false));

          // Calculate which delivery days were changed (added or removed)
          const originalDays = originalDeliveryDaysRef.current;
          const changedDays = calculateChangedDeliveryDays(
            deliveryDays,
            originalDays,
          );
          // Store the changed delivery days for highlighting
          dispatch(setRecentlyEditedDeliveryDays(changedDays));

          // Update the ref with the new saved state for future comparisons
          originalDeliveryDaysRef.current = [...deliveryDays];

          if (id) {
            dispatch(
              snackbarShowMessage(
                `Delivery days for restaurant: ${id} edited `,
              ),
            );
            dispatch(toggleOperationHoursViewMode(true));
          } else {
            setSuccessPopupModal({
              isAlert: true,
              isOpen: true,
              message:
                t('operationHours.success.pendingProcess') +
                ' ' +
                RDResult?.data?.result?.referenceId,
              shouldRedirectOnClose: true,
            });
          }
        } else {
          if (id) {
            dispatch(
              snackbarShowMessage(
                `Error while editing restaurant: ${id} delivery days `,
              ),
            );
          } else {
            dispatch(snackbarShowMessage(t('operationHours.massUpload.error')));
          }
        }
      }
    } catch (error) {
      if (error?.response?.data?.error_code === 'INTERNAL_SERVER_ERROR') {
        setOpenErrorModal(true);
        setErrors(error?.response?.data);
      } else {
        if (id) {
          dispatch(
            snackbarShowMessage(
              `Error while editing restaurant: ${id} delivery days `,
            ),
          );
        } else {
          dispatch(snackbarShowMessage(t('operationHours.massUpload.error')));
        }
      }
    }
  };

  const handleEditionSubmit = async (e: React.MouseEvent) => {
    updateRoutineDays();
  };

  const handleEditionCancel = (e: React.MouseEvent) => {
    if (id) {
      handleEditionCancelForSingleRestaurant();
    } else {
      dispatch(setEditedRoutineDays([]));
    }
    dispatch(setEdited(false));
  };

  function handleEditionCancelForSingleRestaurant() {
    if (deliveryDays.length && isRoutineDaysEdited) {
      dispatch(getRoutineDays(id));
    }
  }

  useEffect(() => {
    if (id) {
      dispatch(getRoutineDays(id));
      // Reset capture flag when restaurant ID changes
      hasCapturedOriginalRef.current = false;
      originalDeliveryDaysRef.current = [];
    }
  }, [dispatch, id]);

  // Capture original delivery days when they're loaded from API (not user edits)
  useEffect(() => {
    if (loaded && !hasCapturedOriginalRef.current) {
      // Only capture once when data is first loaded from API (including empty array)
      originalDeliveryDaysRef.current = [...deliveryDays];
      hasCapturedOriginalRef.current = true;
    }
  }, [loaded, deliveryDays]);

  function shouldDisableRoutineDays() {
    if (!isMDSUpAndRunning) return false;
    if (id) return hasEditHoursOfOperation && !isRestaurantClosed;
    return hasViewMenuItemOperationHours;
  }

  function shouldCancelButtonDisabled() {
    if (!shouldDisableRoutineDays()) return true;
    if (id && isRestaurantClosed) return true;
    if (deliveryDays.length && isRoutineDaysEdited) return false;
    return true;
  }

  function shouldSaveButtonDisabled() {
    if (!shouldDisableRoutineDays()) return true;
    if (id && isRestaurantClosed) return true;
    if (!id && !selectedRestaurants.length) return true;
    if (deliveryDays.length && isRoutineDaysEdited) return false;
    return true;
  }

  const hasEditRoutineDaysPermission = shouldDisableRoutineDays();

  return (
    <React.Fragment>
      <Grid item xs={9} lg={3}>
        <AppBar position="static" className={classes.appBar}>
          <Toolbar variant="dense">
            <Typography>{t('operationHours.selectDays')}</Typography>
          </Toolbar>
        </AppBar>
        <RBIFollowCursorTooltip title={tooltipMessage}>
          <Paper className={classes.paper2}>
            <RoutineDaysWeekSelection
              targetReducer={'RoutineDaysReducer'}
              targetName={'deliveryDays'}
              title={t('operationHours.truckDeliveryDays')}
              hasEditPermission={hasEditRoutineDaysPermission}
            />
          </Paper>
        </RBIFollowCursorTooltip>
        <div>
          <Grid
            container
            direction="row"
            justify="flex-end"
            style={{ marginTop: 8 }}
          >
            <Grid item xs="auto" sm="auto" md="auto" lg="auto" xl="auto">
              <Button
                disabled={shouldCancelButtonDisabled()}
                variant="contained"
                color="primary"
                onClick={handleEditionCancel}
                style={{ marginRight: 10 }}
              >
                {t('app.common.cancel')}
              </Button>
            </Grid>
            <Grid item xs="auto" sm="auto" md="auto" lg="auto" xl="auto">
              <Button
                disabled={shouldSaveButtonDisabled()}
                variant="contained"
                color="primary"
                onClick={handleEditionSubmit}
              >
                {t('app.common.save')}
              </Button>
            </Grid>
          </Grid>
        </div>
      </Grid>
      {successPopupModal.isOpen && (
        <RBISuccessPopup
          isOpen={successPopupModal.isOpen}
          isAlert={successPopupModal.isAlert}
          closeModal={handleSuccessCloseModal}
          message={successPopupModal.message}
        />
      )}
      {openErrorModal && (
        <ShowErrorMessageModal
          isOpen={openErrorModal}
          onClose={() => setOpenErrorModal(false)}
          errors={uploadErrors}
        />
      )}
    </React.Fragment>
  );
}

export default SupplyIntakeDays;

function calculateChangedDeliveryDays(
  deliveryDays: string[],
  originalDeliveryDays: string[],
): string[] {
  const addedDays = deliveryDays.filter(
    day => !originalDeliveryDays.includes(day),
  );
  const removedDays = originalDeliveryDays.filter(
    day => !deliveryDays.includes(day),
  );
  const allChangedDays = [...addedDays, ...removedDays];

  // Extract day name from format "2 - Monday"
  return allChangedDays
    .map(day => {
      const parts = day.split(' - ');
      return parts.length > 1 ? parts[1] : undefined;
    })
    .filter((dayName): dayName is string => dayName !== undefined);
}
